import { useEffect, useState } from "react";
import { AlertTriangle, ArrowRight, CheckCircle2, Hash } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, newIdempotencyKey } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { Card } from "../common/UI";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

function TransitionDialog({ t, m, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [key] = useState(newIdempotencyKey);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/measures/${m.id}/transitions/${t.id}`, { reason: reason || null, row_version: m.row_version }, { headers: { "Idempotency-Key": key } });
      toast.success(`Moved to ${data.stage}${data.official_number && t.assigns_number ? ` · assigned ${data.official_number}` : ""}`);
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="transition-dialog">
        <DialogHeader><DialogTitle>{t.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Move <b>{m.official_number || m.tracking_ref}</b> to <b>{t.to_stage.name}</b>.</p>
        {t.assigns_number && <p className="rounded-md border border-sky-200 bg-sky-50 p-2 text-xs text-sky-800"><Hash className="mr-1 inline h-3.5 w-3.5" />An official number will be allocated atomically from the numbering sequence. It cannot be changed afterwards.</p>}
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">{t.requires_reason ? "Reason / remarks (required)" : "Remarks (optional)"}</span>
          <textarea rows={3} className="otms-input" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="transition-reason-input" />
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || (t.requires_reason && !reason.trim())} onClick={go} className="bg-sky-600 hover:bg-sky-700" data-testid="transition-confirm-button">{busy ? "Processing…" : "Confirm"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function WorkflowPanel({ m, onDone, tick }) {
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null);
  useEffect(() => {
    api.get(`/measures/${m.id}/transitions`).then((r) => setList(r.data.transitions)).catch((e) => toast.error(errMsg(e)));
  }, [m.id, tick]);
  const openTasks = m.tasks.filter((t) => t.status === "open" || t.status === "in_progress");
  return (
    <div className="space-y-4">
      <Card title="Workflow actions" testId="workflow-panel">
        {!list && <div className="text-xs text-slate-400">Loading…</div>}
        {list && list.length === 0 && <div className="text-xs text-slate-400">No further transitions from this stage.</div>}
        <div className="space-y-3">
          {list?.map((t) => (
            <div key={t.id} className="rounded-md border border-slate-200 p-3">
              <Button size="sm" disabled={t.problems.length > 0} onClick={() => setActive(t)} data-testid={`workflow-transition-button-${t.to_stage.code}`}
                className="w-full justify-between bg-slate-900 hover:bg-sky-700">
                <span className="truncate">{t.name}</span><ArrowRight className="h-4 w-4 shrink-0" />
              </Button>
              {t.problems.length > 0 ? (
                <ul className="mt-2 space-y-1" data-testid={`transition-problems-${t.to_stage.code}`}>
                  {t.problems.map((p) => <li key={p} className="flex gap-1.5 text-[11px] text-amber-800"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{p}</li>)}
                </ul>
              ) : (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-700"><CheckCircle2 className="h-3 w-3" />Requirements met</div>
              )}
            </div>
          ))}
        </div>
      </Card>
      <Card title={`Open tasks (${openTasks.length})`} testId="measure-tasks">
        {openTasks.length === 0 && <div className="text-xs text-slate-400">None</div>}
        {openTasks.map((t) => (
          <div key={t.id} className="border-b border-slate-100 py-2 text-xs last:border-0">
            <div className="font-medium text-slate-800">{t.title}</div>
            <div className="text-[11px] text-slate-500">Due {fmtDateTime(t.due_date)}</div>
          </div>
        ))}
      </Card>
      {active && <TransitionDialog t={active} m={m} onClose={() => setActive(null)} onDone={onDone} />}
    </div>
  );
}
